import { Col } from '../../components/custom';

export default function SegmentingCustomers()
{
    return (
        <Col id='segmentingCustomers' className='perspectives'>
            <Col className='banner'>
                <span className='title'>Segmenting Customers by Experience Preferences</span>
                <span className='desc'>One size doesn't fit all — design CX that matches customer expectations.</span>
            </Col>
            <Col className='section'>
                <span className='title'>The Challenge</span>
                <span className='desc bold'>Not all customers want the same thing.</span>
                <span className='descsmall'>After running a comprehensive CX survey across 10,000 users of a digital platform, the organization found that average satisfaction scores didn't tell the full story. Some customers valued speed and autonomy; others preferred detailed guidance and human support. Without segmentation, personalization was impossible — and CX efforts were diluted.</span>
            </Col>
            <Col className='section'>
                <span className='title'>Our Philosophy</span>
                <span className='desc bold'>Great experiences aren't universally defined — they're personally expected.</span>
                <span className='descsmall'>We believe experience design should be grounded in data-driven personas, not assumptions. By clustering quantitative survey responses, organizations can reveal distinct customer groups with unique preferences, behaviors, and needs.</span>
            </Col>
            <Col className='section'>
                <span className='title'>The Segmentation Framework</span>
                <span className='desc'>A clustering-first approach to uncover experience-driven personas:</span>
                <ul>
                    <li className='descsmall'>
                        <strong>CX Survey with Preference Indicators</strong>
                        <ul>
                            <li className='descsmall'>Included Likert-scale items on preferences like speed vs. thoroughness, self-service vs. agent support, communication frequency, and feature usability.</li>
                            <li className='descsmall'>Collected behavioral metadata (e.g., visit frequency, purchase size) alongside.</li>
                        </ul>
                    </li>
                    <li className='descsmall'>
                        <strong>Cluster Analysis & Latent Class Modeling</strong>
                        <ul>
                            <li className='descsmall'>Applied k-means and latent class analysis to uncover natural groupings.</li>
                            <li className='descsmall'>
                                Identified 4 distinct personas:
                                <ul>
                                    <li className='descsmall'><strong>Efficiency Seekers:</strong>Prefer fast, minimal interaction, often abandon if delayed.</li>
                                    <li className='descsmall'><strong>Support-First Users:</strong>Want handholding and rated agent empathy highly.</li>
                                    <li className='descsmall'><strong>Value Maximizers:</strong>Care about clarity of pricing, promotions, and added features.</li>
                                    <li className='descsmall'><strong>Explorers:</strong>Frequent browsers who respond well to personalized content.</li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                    <li className='descsmall'>
                        <strong>Persona Profiling & Validation</strong>
                        <ul>
                            <li className='descsmall'>Mapped each cluster to demographic and behavioral profiles (age group, usage pattern, channel preference).</li>
                            <li className='descsmall'>Cross-referenced segments with NPS and retention metrics for strategic value.</li>
                        </ul>
                    </li>
                    <li className='descsmall'>
                        <strong>Experience Design Playbooks</strong>
                        <ul>
                            <li className='descsmall'>Created tailored CX strategies for each persona — from UI tweaks to support channel prioritization.</li>
                        </ul>
                    </li>
                </ul>
            </Col>
            <Col className='section'>
                <span className='title'>What This Enables</span>
                <ul>
                    <li className='descsmall'><strong>Personalized CX Strategies</strong> — Move from generic journeys to persona-led design.</li>
                    <li className='descsmall'><strong>Smarter Content & Support</strong> — Align outreach, UX, and agent scripts to customer mindset.</li>
                    <li className='descsmall'><strong>Product Prioritization</strong> — Build features that resonate with your highest-value segments.</li>
                    <li className='descsmall'><strong>Marketing Precision</strong> — Use preference profiles to guide targeting and messaging.</li>
                </ul>
            </Col>
            <Col className='section'>
                <span className='title'>The Takeaway</span>
                <span className='desc bold'>When you know what different customers value, you stop guessing — and start designing.</span>
                <span className='descsmall'>Survey-driven segmentation enables companies to deliver CX that feels made for each user — because it is.</span>
            </Col>
        </Col>
    )
}